import type {Identified, Valuable} from 'typings/portaro.be.types';
import type {Pageable} from 'typings/portaro.fe.types';
import type {Observable, Subject} from 'rxjs';
import {ReplaySubject} from 'rxjs';
import {replaceAllById} from 'shared/utils/array-utils.fp';
import {debounceTime} from 'rxjs/operators';

export type LoadListItemsFunction<ITEM extends Identified<ID>, ID> = (pageable: Pageable, ascending?: boolean) => Promise<ITEM[]>;
export type LoadListSizeFunction = () => Promise<Valuable<number>>;

export interface ReadOnlyReactivePageableList<ITEM extends Identified<ID>, ID> {
    isLoaded(): boolean;
    isLoadedAll(): boolean
    getSize(): number;
    isEmpty(): boolean;
    getLoadedItems(): ITEM[];
    getLoadedItem$(): Observable<ITEM[]>;
    loadItems(): Promise<ITEM[]>;
    loadNext(): Promise<ITEM[]>;
    reloadItems(): Promise<ITEM[]>;
    setAscendingSortingOrder(ascending: boolean): void;
    clearLoadedItems(): void;
    getCurrentPage$(): Observable<Pageable>;
}

export class ReactivePageableList<ITEM extends Identified<ID>, ID> implements ReadOnlyReactivePageableList<ITEM, ID>{

    private readonly PAGE_SIZE = 20;
    private readonly FIRST_PAGE = 1;

    private pageToLoad: number;
    private size: number;
    private items: ITEM[];
    private isAscending: boolean;
    private loadedItem$: Subject<ITEM[]> = new ReplaySubject<ITEM[]>(1);
    private page$: Subject<Pageable> = new ReplaySubject<Pageable>(1);

    constructor(private readonly loadListItems: LoadListItemsFunction<ITEM, ID>,
                private readonly loadListSize: LoadListSizeFunction) {
        this.pageToLoad = this.FIRST_PAGE;
        this.isAscending = true;
    }

    public isLoaded(): boolean {
        return !!this.items;
    }

    public isSortedInAscendingOrder(): boolean {
        return this.isAscending;
    }

    public getCurrentPage$():Observable<Pageable> {
        return this.page$.asObservable();
    }

    public getLoadedItems(): ITEM[] {
        return this.items;
    }

    public getLoadedItem$(): Observable<ITEM[]> {
        return this.loadedItem$.pipe(debounceTime(100)); // can execute multiple changes before update
    }

    public isLoadedAll(): boolean {
        if (this.items) {
            return this.items.length >= this.size;
        } else {
            return this.size === 0;
        }
    }

    public getSize(): number {
        return this.size;
    }

    public isEmpty(): boolean {
        return this.getSize() === 0;
    }

    public async loadItems(isAscending = true): Promise<ITEM[]> {
        if (!this.isLoaded()) {
            this.isAscending = isAscending;
            return this.loadFirstPageIfNotAlreadyLoaded();
        }

        if (this.isAscending !== isAscending) {
            this.isAscending = isAscending;
            return this.reloadItems();
        }

        return this.items;
    }

    public async loadNext(): Promise<ITEM[]>{
        if (!this.isLoadedAll()) {
            const items = await this.fetchNextPage();
            this.items = this.items.concat(items);
            this.emitCurrentItems();
        }
        return this.items;
    }

    public async reloadItems(): Promise<ITEM[]>{
        this.clearLoadedItems();
        return this.loadFirstPageIfNotAlreadyLoaded();
    }

    public async remove(toBeRemoved: ITEM): Promise<void> {
        if (this.isLoadedAll()) {
            this.items = this.items.filter((item) => item !== toBeRemoved);
        } else {
            this.items = this.items.filter((item) => item !== toBeRemoved);
            const items = await this.fetchCurrentPage();
            const replacement = items[items.length - 1];
            this.items.push(replacement);
        }
        await this.loadSize();
        this.emitCurrentItems();
    }

    public removeById(id: ID): Promise<void> {
        const toBeRemoved = this.items.find((item) => item.id === id);
        return this.remove(toBeRemoved);
    }

    public removeAll() {
        this.items = [];
        this.size = 0;
        this.pageToLoad = this.FIRST_PAGE;
        this.emitCurrentItems();
    }

    public update(updatedItem: ITEM) {
        replaceAllById(updatedItem)(this.items);
        this.emitCurrentItems();
    }

    public sort(comparator: (x: ITEM, y: ITEM) => number) {
        this.items = this.items.sort(comparator);
        this.emitCurrentItems();
    }

    public setAscendingSortingOrder(ascending: boolean) {
        if (this.isAscending !== ascending) {
            this.isAscending = ascending;
        }
    }

    public clearLoadedItems() {
        this.pageToLoad = this.FIRST_PAGE;
        if (this.items) {
            this.items = null;
            this.size = null;
        }
    }

    private async loadFirstPageIfNotAlreadyLoaded() {
        await this.loadSize();
        if (!this.items) {
            this.items = await this.fetchNextPage();
            this.emitCurrentItems();
        }
        return this.items;
    }

    private emitCurrentItems() {
        this.loadedItem$.next(this.getLoadedItems())
    }

    private async fetchNextPage(): Promise<ITEM[]> {
        const page = this.getRequestPage();
        this.page$.next(page);
        const items = await this.fetchPage(page); // possible race condition
        this.pageToLoad = page.pageNumber + 1;
        return items;
    }

    private fetchCurrentPage(): Promise<ITEM[]> {
        const page = this.getRequestPage(this.pageToLoad - 1);
        return this.fetchPage(page);
    }

    private fetchPage(page: Pageable) {
        return this.loadListItems(page, this.isAscending);
    }

    private async loadSize(): Promise<void> {
        const size = await this.loadListSize();
        this.size = size.value;
    }

    private getRequestPage(pageToLoad?: number): Pageable {
        return {pageNumber: pageToLoad ?? this.pageToLoad, pageSize: this.PAGE_SIZE};
    }
}